import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { faRedo, faTrashCan, faSearch } from '@fortawesome/free-solid-svg-icons';
import Pagination from '../../components/Pagination';
import ConfirmationModal from '../../components/ConfirmationModal';
import TextInput from '../../components/TextInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import clientApi from '../../api/clientApi';
import { useToast } from '../../contexts/ToastContext';

const TrashedClients = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsPerPage] = useState(10);

  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [isPermanentDeleteModalOpen, setIsPermanentDeleteModalOpen] = useState(false);
  const [clientToActOnId, setClientToActOnId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [error, setError] = useState(null);

  const [lastPage, setLastPage] = useState(1);

  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page')) || 1;

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  const fetchTrashedClients = useCallback(async (page, perPage, search) => {
    setLoading(true);
    setError(null);
    try {
      const response = await clientApi.getClients({ page, perPage, search, includeTrashed: true });
      setClients(response.data);
      setLastPage(response.pagination.last_page);
    } catch (err) {
      console.error('Failed to fetch trashed clients:', err);
      setError('Failed to load trashed clients. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrashedClients(currentPage, itemsPerPage, debouncedSearchTerm);
  }, [fetchTrashedClients, currentPage, itemsPerPage, debouncedSearchTerm]);

  const handleRestoreClick = (id) => {
    setClientToActOnId(id);
    setIsRestoreModalOpen(true);
  };

  const handleConfirmRestore = async () => {
    setIsRestoreModalOpen(false);
    if (clientToActOnId) {
      setLoading(true);
      setError(null);
      try {
        await clientApi.restoreClient(clientToActOnId);
        addToast('Client restored successfully!', 'success');
        await fetchTrashedClients(currentPage, itemsPerPage, debouncedSearchTerm);
      } catch (err) {
        console.error('Failed to restore client:', err);
        addToast(err.response?.data?.message || 'Failed to restore client. Please try again.', 'error');
        setError('Failed to restore client. Please try again.');
      } finally {
        setLoading(false);
        setClientToActOnId(null);
      }
    }
  };

  const handlePermanentDeleteClick = (id) => {
    setClientToActOnId(id);
    setIsPermanentDeleteModalOpen(true);
  };

  const handleConfirmPermanentDelete = async () => {
    setIsPermanentDeleteModalOpen(false);
    if (clientToActOnId) {
      setLoading(true);
      setError(null);
      try {
        await clientApi.permanentDeleteClient(clientToActOnId);
        addToast('Client permanently deleted successfully!', 'success');
        await fetchTrashedClients(currentPage, itemsPerPage, debouncedSearchTerm);
      } catch (err) {
        console.error('Failed to permanently delete client:', err);
        addToast(err.response?.data?.message || 'Failed to permanently delete client. Please try again.', 'error');
        setError('Failed to permanently delete client. Please try again.');
      } finally {
        setLoading(false);
        setClientToActOnId(null);
      }
    }
  };

  const handleCancelAction = () => {
    setIsRestoreModalOpen(false);
    setIsPermanentDeleteModalOpen(false);
    setClientToActOnId(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white flex flex-col min-h-[calc(100vh-100px)]">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-text-white">Trashed Clients</h1>
        <Button
          onClick={() => navigate('/clients')}
          className="bg-accent-red hover:bg-red-700 text-white"
        >
          Back to Clients
        </Button>
      </div>

      <div className="mb-4">
        <TextInput
          label="Search Trashed Clients"
          id="search-trashed-clients"
          name="search-trashed-clients"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by client name..."
          icon={faSearch}
        />
      </div>

      {loading ? (
        <SkeletonLoader type="table" count={itemsPerPage} />
      ) : error ? (
        <div className="text-red-500 text-center py-4">{error}</div>
      ) : (
        <>
          <div className="overflow-x-auto flex-grow">
            <table className="min-w-full bg-gray-800 border border-gray-700">
              <thead>
                <tr>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">ID</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Name</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Logo</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Actions</th>
                </tr>
              </thead>
              <tbody>
                {clients.length > 0 ? (
                  clients.map((client) => (
                    <tr key={client.id} className="hover:bg-gray-700">
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{client.id}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{client.name}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">
                        {client.logo && (
                          <img src={client.logo} alt={client.name} className="h-10 w-10 object-contain" />
                        )}
                      </td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm flex items-center flex-nowrap">
                        <Button
                          onClick={() => handleRestoreClick(client.id)}
                          className="text-green-500 hover:text-green-600 mr-3"
                          icon={faRedo}
                          tooltipId={`restore-client-tooltip-${client.id}`}
                          tooltipContent="Restore"
                        />
                        <Button
                          onClick={() => handlePermanentDeleteClick(client.id)}
                          className="text-red-600 hover:text-red-700"
                          icon={faTrashCan}
                          tooltipId={`permanent-delete-client-tooltip-${client.id}`}
                          tooltipContent="Permanent Delete"
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="py-3 px-4 text-center text-sm text-text-gray">No trashed clients found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={lastPage}
          />
        </>
      )}

      <ConfirmationModal
        isOpen={isRestoreModalOpen}
        onClose={handleCancelAction}
        onConfirm={handleConfirmRestore}
        title="Confirm Restore"
        message="Are you sure you want to restore this client?"
      />

      <ConfirmationModal
        isOpen={isPermanentDeleteModalOpen}
        onClose={handleCancelAction}
        onConfirm={handleConfirmPermanentDelete}
        title="Confirm Permanent Delete"
        message="Are you sure you want to permanently delete this client? This action cannot be undone."
      />
    </div>
  );
};

export default TrashedClients;